import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, Users, Send, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useNavigate } from "react-router-dom";
import { WizardAgendamento } from "@/components/WizardAgendamento/WizardAgendamento";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { fetchSpecialtiesByClinic, DentalSpecialty } from "@/services/dental-specialty-service";
import { sendBookingInvite } from "@/services/public-scheduling-service";
import {
  Patient,
  CreatePatientData,
  fetchPatients,
  createPatient,
  updatePatient,
  activatePatient,
  inactivatePatient,
  deletePatient
} from "@/services/patient-service";

import { useClinic } from "@/contexts/ClinicContext";
import { PatientFilters, PatientFiltersData } from "@/components/patients/PatientFilters";
import { PatientsTable } from "@/components/patients/PatientsTable";
import { CreateEditPatientDialog } from "@/components/patients/CreateEditPatientDialog";
import { DeletePatientDialog } from "@/components/patients/DeletePatientDialog";

export default function Patients() {
  const { selectedClinicId } = useClinic();
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [filters, setFilters] = useState<PatientFiltersData>({});
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isCreateEditDialogOpen, setIsCreateEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  // Modal Convite de Agendamento
  const [isSendLinkOpen, setIsSendLinkOpen] = useState(false);
  const [linkSpecialties, setLinkSpecialties] = useState<DentalSpecialty[]>([]);
  const [linkSpecialtyId, setLinkSpecialtyId] = useState('');
  const [linkDoubleSlot, setLinkDoubleSlot] = useState(false);
  const [linkLoadingSpecialties, setLinkLoadingSpecialties] = useState(false);
  const [linkSubmitting, setLinkSubmitting] = useState(false);

  const [isWizardOpen, setIsWizardOpen] = useState(false);


  const { toast } = useToast();

  const loadPatients = async () => {
    setLoading(true);
    try {
      const data = await fetchPatients(
        includeInactive,
        false,
        undefined,
        currentPage,
        filters.pageSize || 20,
        filters.nameLike,
        filters.cpf,
        filters.birthDate,
        filters.email,
        filters.cellPhoneLike,
        selectedClinicId ?? undefined
      );
      setPatients(data);
    } catch (error) {
      toast({
        title: "Erro ao carregar pacientes",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPatients();
  }, [filters, includeInactive, currentPage, selectedClinicId]);

  const navigate = useNavigate();

  const handleViewRecord = (patient: Patient) => {
    console.log('handleViewRecord')
    // Isso vai abrir a aba do prontuário usando a lógica que criamos no AppLayout
    navigate(`/prontuario/${patient.id}`);
  };

  // Handlers originais mantidos...
  const handleFilterChange = (newFilters: PatientFiltersData) => { setFilters(newFilters); setCurrentPage(1); };
  const handleCreateNew = () => { setSelectedPatient(null); setIsCreateEditDialogOpen(true); };
  const handleEdit = (patient: Patient) => { setSelectedPatient(patient); setIsCreateEditDialogOpen(true); };
  const handleDelete = (patient: Patient) => { setSelectedPatient(patient); setIsDeleteDialogOpen(true); };

  const handleSave = async (data: CreatePatientData, id?: string) => {
    try {
      if (id) {
        await updatePatient(id, data);
        toast({ title: "Paciente atualizado!" });
      } else {
        await createPatient(data);
        toast({ title: "Paciente criado!" });
      }
      loadPatients();
    } catch (error) {
      toast({
        title: id ? "Erro ao atualizar" : "Erro ao criar",
        description: error instanceof Error ? error.message : "Erro desconhecido",
        variant: "destructive",
      });
      throw error;
    }
  };

  const handleOpenWizard = (patient: Patient) => {
    setSelectedPatient(patient);
    setIsWizardOpen(true);
  };

  const handleOpenSendLink = async (patient: Patient) => {
    setSelectedPatient(patient);
    setLinkSpecialtyId('');
    setLinkDoubleSlot(false);
    setIsSendLinkOpen(true);
    if (selectedClinicId) {
      setLinkLoadingSpecialties(true);
      try {
        const data = await fetchSpecialtiesByClinic(selectedClinicId);
        setLinkSpecialties(data);
      } catch {
        toast({ title: "Erro ao carregar especialidades", variant: "destructive" });
      } finally {
        setLinkLoadingSpecialties(false);
      }
    }
  };

  const handleSendLink = async () => {
    if (!selectedPatient || !linkSpecialtyId || !selectedClinicId) return;
    setLinkSubmitting(true);
    try {
      await sendBookingInvite({
        clinic_id: selectedClinicId,
        cellphone: selectedPatient.cellphone,
        patient_id: selectedPatient.id,
        specialty_id: linkSpecialtyId,
      });
      toast({ title: "Convite enviado pelo WhatsApp! 📲", description: "O paciente receberá a mensagem em instantes." });
      setIsSendLinkOpen(false);
    } catch (err: any) {
      toast({ title: "Erro ao enviar convite", description: err.message || "Não foi possível enviar o convite.", variant: "destructive" });
    } finally {
      setLinkSubmitting(false);
    }
  };


  const handleConfirmDelete = async (id: string) => {
    try {
      await deletePatient(id);
      toast({ title: "Paciente deletado!" });
      loadPatients();
    } catch (error) {
      toast({ title: "Erro ao deletar", variant: "destructive" });
    }
  };

  const handleActivate = async (patient: Patient) => {
    try {
      await activatePatient(patient.id);
      toast({ title: "Paciente ativado!" });
      loadPatients();
    } catch (error) {
      toast({ title: "Erro ao ativar", variant: "destructive" });
    }
  };

  const handleInactivate = async (patient: Patient) => {
    try {
      await inactivatePatient(patient.id);
      toast({ title: "Paciente inativado!" });
      loadPatients();
    } catch (error) {
      toast({ title: "Erro ao inativar", variant: "destructive" });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2">
              <Users className="page-icon" />
              <h1 className="page-title">Pacientes</h1>
            </div>
          <p className="page-subtitle">Gerencie os pacientes cadastrados</p>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadPatients} disabled={loading} className="gap-1.5 h-7 text-xs">
            <RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button size="sm" onClick={handleCreateNew} className="gap-1.5 h-7 text-xs"><Plus className="h-3 w-3" /> Novo</Button>
        </div>
      </div>

      <PatientFilters
        onFilterChange={handleFilterChange}
        includeInactive={includeInactive}
        onIncludeInactiveChange={setIncludeInactive}
      />

      <div className="space-y-2">
        <p className="page-counter">{patients.length} paciente(s) encontrado(s)</p>
        
        <PatientsTable
          patients={patients}
          loading={loading}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onActivate={handleActivate}
          onInactivate={handleInactivate}
          onInviteSchedule={handleOpenWizard}
          onViewRecord={handleViewRecord}
          onSendLink={handleOpenSendLink}
        />
        <div className="flex items-center justify-between border rounded-lg px-4 py-2 bg-card">
          <span className="page-counter">Página {currentPage}</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" className="btn-header" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Anterior</Button>
            <Button size="sm" variant="outline" className="btn-header" onClick={() => setCurrentPage(p => p + 1)} disabled={patients.length < (filters.pageSize || 20)}>Próxima</Button>
          </div>
        </div>
      </div>
      {/* Modal Convite de Agendamento */}
      <Dialog open={isSendLinkOpen} onOpenChange={(open) => { if (!open) setIsSendLinkOpen(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-teal-600" />
              Convite de Agendamento
            </DialogTitle>
          </DialogHeader>
          {selectedPatient && (
            <div className="space-y-4 py-2">
              <div className="bg-slate-50 rounded-lg px-4 py-3 text-sm">
                <p className="font-semibold text-slate-700">{selectedPatient.name}</p>
                <p className="text-slate-500">{selectedPatient.cellphone.replace(/\D/g, "").replace(/^55/, "").replace(/^(\d{2})(\d{5})(\d{4})$/, "($1) $2-$3").replace(/^(\d{2})(\d{4})(\d{4})$/, "($1) $2-$3")}</p>
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Especialidade</Label>
                {linkLoadingSpecialties ? (
                  <div className="flex items-center gap-2 text-slate-500 text-sm">
                    <Loader2 className="h-4 w-4 animate-spin" /> Carregando especialidades...
                  </div>
                ) : (
                  <select
                    value={linkSpecialtyId}
                    onChange={(e) => setLinkSpecialtyId(e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  >
                    <option value="">Selecione uma especialidade...</option>
                    {linkSpecialties.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-sm font-semibold">Duração da Consulta</Label>
                <div className="flex gap-3">
                  <button type="button" onClick={() => setLinkDoubleSlot(false)}
                    className={`flex-1 py-2 px-4 rounded-lg border text-sm font-semibold transition-all ${!linkDoubleSlot ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'}`}>
                    30 minutos
                  </button>
                  <button type="button" onClick={() => setLinkDoubleSlot(true)}
                    className={`flex-1 py-2 px-4 rounded-lg border text-sm font-semibold transition-all ${linkDoubleSlot ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-400'}`}>
                    1 hora
                  </button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => setIsSendLinkOpen(false)} disabled={linkSubmitting}>Cancelar</Button>
            <Button onClick={handleSendLink} disabled={!linkSpecialtyId || linkSubmitting || linkLoadingSpecialties}
              className="bg-teal-600 hover:bg-teal-700 text-white gap-2">
              {linkSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Enviando...</> : <><Send className="h-4 w-4" /> Enviar Convite</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CreateEditPatientDialog
        patient={selectedPatient}
        open={isCreateEditDialogOpen}
        onOpenChange={setIsCreateEditDialogOpen}
        onSave={handleSave}
      />

      <DeletePatientDialog
        patient={selectedPatient}
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleConfirmDelete}
      />

  {isWizardOpen && selectedPatient && (
    <WizardAgendamento
      // PASSE O OBJETO COMPLETO AQUI
      patient={selectedPatient} 
      // Mantenha o restante como está
      onClose={() => {
        setIsWizardOpen(false);
        setSelectedPatient(null);
      }}
      onSuccess={() => {
        loadPatients();
        toast({
          title: "Sucesso!",
          description: "Agendamento realizado com sucesso através do assistente.",
        });
      }}
    />
  )}

    </div>
  );
}